import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import {
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { GetProductCollectionApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { GetWholegoodCollectionApiParameter } from "@store/Services/PartsService";
import { getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import loadWholegoods from "@store/Store/Data/Parts/Handlers/LoadWholegoods";
import { getWholegoodsDataView } from "@store/Store/Data/Parts/PartsSelectors";
import { StoreHandlerProps } from "@store/Store/HandlerCreator";

export interface DisplayWholegoodsResult {
    wholegoods?: ProductModel[];
    products?: ProductModel[];
    productInfosByProductId: SafeDictionary<ProductInfo>;
}

type Parameter = GetWholegoodCollectionApiParameter & HasOnSuccess;
type Props = StoreHandlerProps & {
    wholegoodsApiParameter: GetWholegoodCollectionApiParameter;
    productsApiParameter: GetProductCollectionApiV2Parameter;
    result: DisplayWholegoodsResult;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginLoad: HandlerType = props => {
    props.dispatch({
        type: "Store/Pages/ProductDetails/Wholegoods/BeginLoad",
    });

    props.result = {
        productInfosByProductId: {},
    };
};

export const PopulateWholegoodsApiParameter: HandlerType = props => {
    const { onSuccess, ...parameter } = props.parameter;
    props.wholegoodsApiParameter = { ...parameter };
};

export const DispatchSetParameter: HandlerType = props => {
    props.dispatch({
        type: "Store/Pages/ProductDetails/Wholegoods/SetParameter",
        parameter: props.wholegoodsApiParameter,
    });
};

export const DispatchLoadWholegoods: HandlerType = async props => {
    props.result.wholegoods = getWholegoodsDataView(props.getState(), props.wholegoodsApiParameter)?.wholegoods;

    if (props.result.wholegoods) {
        return;
    }

    props.result.wholegoods = await executeAwaitableHandlerChain(loadWholegoods, props.wholegoodsApiParameter, props);
};

export const PopulateProductsApiParameter: HandlerType = props => {
    const settings = getStoreSettingsCollection(props.getState());

    props.productsApiParameter = {
        productIds: props.result.wholegoods?.filter(x => x.id).map(x => x.id),
        pageSize: props.wholegoodsApiParameter.pageSize || settings.poolcorpSettings.partsDefaultPageSize,
    };
};

export const DispatchLoadProducts: HandlerType = async props => {
    if (props.productsApiParameter.productIds?.length) {
        props.result.products = getProductsDataView(props.getState(), props.productsApiParameter).value;

        if (props.result.products) {
            return;
        }

        props.result.products = await executeAwaitableHandlerChain(loadProducts, props.productsApiParameter, props);
    }
};

export const SetUpProductInfos: HandlerType = props => {
    props.result.products?.forEach(product => {
        props.result.productInfosByProductId[product.id] = createFromProduct(product);
    });
};

export const DispatchCompleteLoad: HandlerType = props => {
    props.dispatch({
        type: "Store/Pages/ProductDetails/Wholegoods/CompleteLoad",
        result: props.result,
    });
};

export const LoadRealTimePrices: HandlerType = props => {
    const {
        result: { productInfosByProductId },
    } = props;
    const productIds = Object.keys(productInfosByProductId);
    if (productIds.length === 0) {
        return;
    }

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: productIds.map(x => productInfosByProductId[x]!),
            onSuccess: realTimePricing => {
                props.dispatch({
                    type: "Store/Pages/ProductDetails/Wholegoods/CompleteLoadRealTimePricing",
                    realTimePricing,
                });
            },
            onError: () => {
                props.dispatch({
                    type: "Store/Pages/ProductDetails/Wholegoods/FailedLoadRealTimePricing",
                });
            },
        }),
    );
};

export const LoadRealTimeInventory: HandlerType = props => {
    if (!props.result.products?.length) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds: props.result.products.map(o => o.id),
            expand: ["warehouses"], // Poolcorp Custom Code
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "Store/Pages/ProductDetails/CompleteLoadRealTimeInventory",
                    realTimeInventory,
                });
            },
        }),
    );
};

export const chain = [
    DispatchBeginLoad,
    PopulateWholegoodsApiParameter,
    DispatchSetParameter,
    DispatchLoadWholegoods,
    PopulateProductsApiParameter,
    DispatchLoadProducts,
    SetUpProductInfos,
    DispatchCompleteLoad,
    LoadRealTimePrices,
    LoadRealTimeInventory,
];

const displayWholegoods = createHandlerChainRunner(chain, "DisplayWholegoods");
export default displayWholegoods;
